import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { authApi } from '../../services/api';
import './AuthForms.css';

export const ResetPassword: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [token, setToken] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    const resetToken = searchParams.get('token');
    if (resetToken) {
      setToken(resetToken);
    } else {
      setError('Invalid reset link');
    }
  }, [searchParams]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    setSuccess('');

    // Validate passwords match
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      setIsLoading(false);
      return;
    }

    // Validate password length
    if (password.length < 6) {
      setError('Password must be at least 6 characters long');
      setIsLoading(false);
      return;
    }

    try {
      const response = await authApi.resetPassword(token, password);
      setSuccess(response.message);
      // Clear form
      setPassword('');
      setConfirmPassword('');
      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/app/login');
      }, 3000);
    } catch (error: any) {
      setError(error.response?.data?.error || 'Failed to reset password');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToLogin = () => {
    navigate('/app/login');
  };

  if (!token) {
    return (
      <div className="auth-container">
        <div className="auth-card">
          <div className="auth-form">
            <div className="auth-header">
              <h2>
                <span className="logo-accent">todo</span>
                <span>bananowe.it</span>
              </h2>
              <p>Password Reset</p>
            </div>

            <div className="auth-form-content">
              <div className="auth-error">
                Invalid reset link
              </div>
              
              <button 
                type="button" 
                className="auth-secondary-btn"
                onClick={handleBackToLogin}
                style={{ marginTop: '20px' }}
              >
                Back to Sign In
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-form">
          <div className="auth-header">
            <h2>
              <span className="logo-accent">todo</span>
              <span>bananowe.it</span>
            </h2>
            <p>Set your new password</p>
          </div>

          <form onSubmit={handleSubmit} className="auth-form-content">
            {error && (
              <div className="auth-error">
                {error}
              </div>
            )}

            {success && (
              <div className="auth-success">
                {success}
                <p style={{ marginTop: '12px', fontSize: '14px' }}>
                  Redirecting to login page in 3 seconds...
                </p>
              </div>
            )}

            {!success && (
              <>
                <div className="form-group">
                  <label htmlFor="password">New Password</label>
                  <input
                    type="password"
                    id="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    placeholder="Enter your new password (min. 6 characters)"
                    disabled={isLoading}
                    minLength={6}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="confirmPassword">Confirm New Password</label>
                  <input
                    type="password"
                    id="confirmPassword"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    placeholder="Confirm your new password"
                    disabled={isLoading}
                  />
                </div>

                <button 
                  type="submit" 
                  className="auth-submit-btn"
                  disabled={isLoading}
                >
                  {isLoading ? 'Updating Password...' : 'Update Password'}
                </button>

                <div className="auth-divider">
                  <span>Remember your password?</span>
                </div>

                <button 
                  type="button" 
                  className="auth-secondary-btn"
                  onClick={handleBackToLogin}
                >
                  Back to Sign In
                </button>
              </>
            )}
          </form>
        </div>
      </div>
    </div>
  );
}; 